import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Container, Card } from 'react-bootstrap';
import { fetchEcommerceProdutos, checkPendingTasks } from '../../services/api';
import ProductTable from '../../components/ProductTable/ProductTable';
import ProductCard from '../../components/ProductCard/ProductCard';

import FiltrosEcommerce from '../../components/FiltrosEcommerce/FiltrosEcommerce';
import Pagination from '../../components/Pagination/Pagination';
import Loading from '../../components/Loading/Loading';
import EmptyState from '../../components/EmptyState/EmptyState';

import TaskModal from '../../components/TaskModal/TaskModal';
import InfoModal from '../../components/InfoModal/InfoModal';
import styles from './EcommerceCatalog.module.css';
import productTableStyles from '../../components/ProductTable/ProductTable.module.css';
import ProductImage from '../../components/ProductImage/ProductImage';

import ViewToggle from '../../components/ViewToggle/ViewToggle';
import { saveCatalogState, getCatalogState, clearCatalogState } from '../../hooks/useCatalogState';

import { useCarrinhoCompras } from '../../contexts/CarrinhoComprasContext';
import ModalAdicionarItem from '../../components/ModalAdicionarItem/ModalAdicionarItem';
import ListaComprasPanel from '../../components/ListaComprasPanel/ListaComprasPanel';
import ComprasAutomaticasModal from '../../components/ComprasAutomaticas/ComprasAutomaticasModal';
import AlertasVendasModal from '../../components/AlertasVendas/AlertasVendasModal';
import ProductHoverCard from '../../components/ProductHoverCard/ProductHoverCard';
import FaturamentoHeader from '../../components/FaturamentoHeader/FaturamentoHeader';

const EcommerceCatalog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isInitialized = useRef(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState('');
  const [fornecedor, setFornecedor] = useState('');
  const [informacao, setInformacao] = useState('');
  const [infoFilter, setInfoFilter] = useState('all'); // 'all', 'recorrente', 'nao_comprar'
  const [sortConfig, setSortConfig] = useState({ field: 'vendasMesAtual', direction: 'desc' });
  const [viewMode, setViewMode] = useState('grid'); // 'table' or 'grid' - grade como padrão
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [mediaDia, setMediaDia] = useState('');
  // Filtro "não vendidos no período" — separado do sortConfig para sobreviver a um
  // clique de ordenação no cabeçalho da tabela
  const [apenasNaoVendidos, setApenasNaoVendidos] = useState(false);
  // Opção escolhida no dropdown de Ordenação (guardada para o select refletir o estado
  // restaurado ao voltar do Detalhe do produto)
  const [ordenacao, setOrdenacao] = useState('');
  const [codigosLote, setCodigosLote] = useState([]);
  const [pendingTasksMap, setPendingTasksMap] = useState({});

  // Task Modal State
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskProduct, setTaskProduct] = useState(null);

  // Info Modal State
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoProduct, setInfoProduct] = useState(null);

  // Tooltip State
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [activeProduct, setActiveProduct] = useState(null);

  // Lista de Compras
  const { modoAtivo, toggleModo, totalItens, adicionarItem, estaNoCarrinho, getQuantidade } = useCarrinhoCompras();
  const [produtoSelecionado, setProdutoSelecionado] = useState(null); // produto para o modal de quantidade
  const [painelAberto, setPainelAberto] = useState(false);
  const [comprasAutoAberto, setComprasAutoAberto] = useState(false); // modal Compras Automáticas
  const [alertasAberto, setAlertasAberto] = useState(false); // modal Alertas de Vendas

  const handleClicarProduto = useCallback((produto) => {
    if (!modoAtivo) return;
    setProdutoSelecionado(produto);
  }, [modoAtivo]);

  const handleFecharModalAdicionar = useCallback(() => {
    setProdutoSelecionado(null);
  }, []);

  const skipNextSave = useRef(false);

  // Restaurar estado salvo ao montar o componente
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const savedState = getCatalogState('ecommerce');
    if (savedState) {
      // Pula o próximo save — ele rodaria com state=defaults (batched setState
      // ainda não aplicou) e sobrescreveria o sessionStorage com defaults.
      skipNextSave.current = true;
      console.log('Restaurando estado do catálogo:', savedState);
      if (savedState.page) setPage(savedState.page);
      if (savedState.search) setSearch(savedState.search);
      if (savedState.categoria) setCategoria(savedState.categoria);
      if (savedState.fornecedor) setFornecedor(savedState.fornecedor);
      if (savedState.informacao) setInformacao(savedState.informacao);
      if (savedState.infoFilter) setInfoFilter(savedState.infoFilter);
      if (savedState.sortConfig) setSortConfig(savedState.sortConfig);
      if (savedState.apenasNaoVendidos) setApenasNaoVendidos(true);
      if (savedState.ordenacao) setOrdenacao(savedState.ordenacao);
      if (savedState.viewMode) setViewMode(savedState.viewMode);
      if (savedState.dataInicio) setDataInicio(savedState.dataInicio);
      if (savedState.dataFim) setDataFim(savedState.dataFim);

      // Restaurar posição do scroll após um pequeno delay para os dados carregarem
      if (savedState.scrollY) {
        setTimeout(() => {
          window.scrollTo({ top: savedState.scrollY, behavior: 'auto' });
        }, 500);
      }

      // NÃO clearCatalogState — será sobrescrito pelo próximo save normal.
    }
  }, []);

  // Salvar estado SEMPRE que qualquer filtro mudar (para estar pronto quando navegar)
  useEffect(() => {
    // Não salvar durante a inicialização
    if (!isInitialized.current) return;
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }

    saveCatalogState('ecommerce', {
      page,
      search,
      categoria,
      fornecedor,
      informacao,
      infoFilter,
      sortConfig,
      viewMode,
      dataInicio,
      dataFim,
      apenasNaoVendidos,
      ordenacao
    });
  }, [page, search, categoria, fornecedor, informacao, infoFilter, sortConfig, viewMode, dataInicio, dataFim, apenasNaoVendidos, ordenacao]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ecommerce-produtos', page, search, categoria, fornecedor, informacao, sortConfig.field, sortConfig.direction, dataInicio, dataFim, codigosLote, mediaDia, apenasNaoVendidos],
    queryFn: () => fetchEcommerceProdutos({
      page,
      search,
      categoria,
      fornecedor,
      informacao,
      sort: sortConfig.field,
      order: sortConfig.direction,
      dataInicio,
      dataFim,
      codigosLote: codigosLote.join(','),
      mediaDia,
      naoVendidos: apenasNaoVendidos
    }),
    staleTime: 0, // Sempre considerar dados como stale
    gcTime: 0, // Não manter dados em cache (garbage collect immediately)
    refetchOnMount: 'always', // Sempre refetch ao montar
    refetchOnWindowFocus: true // Refetch quando a janela ganha foco
  });

  // Verificar tarefas pendentes quando os produtos mudarem
  useEffect(() => {
    const verifyTasks = async () => {
      if (data?.data && data.data.length > 0) {
        const codes = data.data.map(p => p.codigo);
        const result = await checkPendingTasks(codes);
        if (result.success) {
          const map = {};
          result.data.forEach(code => {
            map[code] = true;
          });
          setPendingTasksMap(map);
        }
      } else {
        setPendingTasksMap({});
      }
    };

    verifyTasks();
    verifyTasks();
  }, [data]);

  // Sincronizar busca com URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('search');
    if (searchParam !== null && searchParam !== search) {
      setSearch(searchParam);
      setPage(1);
    }
  }, [location.search]);

  // Abrir modal automaticamente se solicitado via URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const actionParam = params.get('action');
    const searchParam = params.get('search');

    if (actionParam === 'open_obs' && searchParam && data?.data) {
      const found = data.data.find(p =>
        String(p.codigo) === String(searchParam) ||
        String(p.codigoInterno) === String(searchParam)
      );

      if (found) {
        setTaskProduct(found);
        setTaskModalOpen(true);
      }
    }
  }, [data, location.search]);

  const handleSearch = useCallback((searchTerm) => {
    setSearch(searchTerm);
    setPage(1); // Reset para primeira página ao buscar
  }, []);

  const handleFilterChange = useCallback(({ categoria: newCategoria, informacao: newInformacao, fornecedor: newFornecedor, ordenacao: newOrdenacao, dataInicio: newDataInicio, dataFim: newDataFim, codigosLote: newCodigosLote, mediaDia: newMediaDia }) => {
    setCategoria(newCategoria || '');
    setInformacao(newInformacao || '');
    setFornecedor(newFornecedor || '');
    setDataInicio(newDataInicio || '');
    setDataFim(newDataFim || '');
    if (newMediaDia !== undefined) {
      setMediaDia(newMediaDia || '');
    }
    if (newCodigosLote !== undefined) {
      setCodigosLote(newCodigosLote || []);
    }

    // "Não Vendidos" é filtro, não só ordenação: liga/desliga junto com o dropdown
    setApenasNaoVendidos(newOrdenacao === 'nao_vendidos');
    setOrdenacao(newOrdenacao || '');

    // Aplicar ordenação se fornecida
    if (newOrdenacao) {
      switch (newOrdenacao) {
        case 'total_vendas':
          setSortConfig({ field: 'totalVendas', direction: 'desc' });
          break;
        case 'mais_vendidos_mes':
          setSortConfig({ field: 'vendasMesAtual', direction: 'desc' });
          break;
        case 'mais_vendidos_periodo':
          setSortConfig({ field: 'vendasPeriodo', direction: 'desc' });
          break;
        case 'menor_estoque':
          setSortConfig({ field: 'estoque', direction: 'asc' });
          break;
        case 'maior_estoque':
          setSortConfig({ field: 'estoque', direction: 'desc' });
          break;
        case 'maior_lucro':
          setSortConfig({ field: 'lucro', direction: 'desc' });
          break;
        case 'maior_markup':
          setSortConfig({ field: 'markup', direction: 'desc' });
          break;
        case 'menor_markup':
          setSortConfig({ field: 'markup', direction: 'asc' });
          break;
        case 'maior_media_dia':
          setSortConfig({ field: 'mediaDiariaAjustada', direction: 'desc' });
          break;
        case 'menor_media_dia':
          setSortConfig({ field: 'mediaDiariaAjustada', direction: 'asc' });
          break;
        // Produtos SEM nenhuma venda no período selecionado (ou no mês atual, se não houver
        // período). O backend inverte o filtro de vendas e ordena por capital parado.
        case 'nao_vendidos':
          setSortConfig({ field: 'nao_vendidos', direction: 'desc' });
          break;
        default:
          setSortConfig({ field: 'vendasMesAtual', direction: 'desc' });
      }
    } else {
      // Resetar para ordenação padrão
      setSortConfig({ field: 'vendasMesAtual', direction: 'desc' });
    }

    setPage(1);
  }, []);

  const handleFilterInfo = useCallback(() => {
    // Ciclo: all → recorrente → nao_comprar → all
    setInfoFilter(prevFilter => {
      if (prevFilter === 'all') return 'recorrente';
      if (prevFilter === 'recorrente') return 'nao_comprar';
      return 'all';
    });
    setPage(1);
  }, []);

  const handleExportCSV = useCallback(() => {
    if (!data?.data || data.data.length === 0) {
      alert('Nenhum produto para exportar.');
      return;
    }

    const produtos = data.data;
    const headers = ['CODIGO DO PRODUTO', 'FORNECEDOR', 'CATEGORIA', 'DESCRIÇÃO DO PRODUTO', 'VALOR VENDA', 'VALOR COMPRA', 'MARKUP (%)', 'LUCRO MÊS (R$)'];

    const csvRows = [
      headers.join(';'),
      ...produtos.map(p => {
        const precoVenda = parseFloat(p.preco) || 0;
        const precoCusto = parseFloat(p.precoCusto) || 0;
        const markup = precoCusto > 0 ? (((precoVenda - precoCusto) / precoCusto) * 100) : 0;
        const lucroMes = parseFloat(p.lucroReais) || 0;

        return [
          p.codigo || p.codigoInterno || '',
          (p.fornecedor || '').replace(/;/g, ','),
          (p.categoria || '').replace(/;/g, ','),
          (p.nome || p.descricao || '').replace(/;/g, ','),
          precoVenda.toFixed(2).replace('.', ','),
          precoCusto.toFixed(2).replace('.', ','),
          markup.toFixed(1).replace('.', ','),
          lucroMes.toFixed(2).replace('.', ',')
        ].join(';');
      })
    ];

    const csvContent = '\uFEFF' + csvRows.join('\n'); // BOM para UTF-8
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `produtos_ecommerce_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [data]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSort = useCallback((field) => {
    setSortConfig(prevConfig => {
      // Se clicar no mesmo campo, inverte a direção
      if (prevConfig.field === field) {
        return {
          field,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      // Se clicar em novo campo, começa com ordem decrescente para campos de vendas/estoque
      const fieldsDescFirst = ['totalVendas', 'mediaMensal', 'estoque', 'estoqueMinimo', 'lucro', 'vendasMesAtual'];
      return {
        field,
        direction: fieldsDescFirst.includes(field) ? 'desc' : 'asc'
      };
    });
  }, []);

  const handleOpenTaskModal = useCallback((produto) => {
    setTaskProduct(produto);
    setTaskModalOpen(true);
  }, []);

  const handleOpenInfoModal = useCallback((produto) => {
    setInfoProduct(produto);
    setInfoModalOpen(true);
  }, []);

  // Tooltip Handlers
  const handleMouseEnter = useCallback((event, produto) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 340;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const margin = 10;

    let left = rect.left - tooltipWidth - 15;
    if (left < margin) {
      left = rect.right + 15;
    }
    if (left + tooltipWidth > windowWidth - margin) {
      left = windowWidth - tooltipWidth - margin;
    }

    const maxTooltipHeight = Math.floor(windowHeight * 0.9);
    let top = margin;
    if (rect.top < windowHeight / 2) {
      top = Math.max(margin, rect.top);
    } else {
      top = Math.max(margin, windowHeight - maxTooltipHeight - margin);
    }

    setTooltipPosition({
      top,
      left,
      maxHeight: maxTooltipHeight
    });
    setActiveProduct(produto);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setActiveProduct(null);
  }, []);

  const getImageSrc = (produto) => {
    return produto.imagemURL; // Since this is ecommerce catalog, always imagemURL
  };

  const productosList = data?.data || [];



  // Primeiro: filtrar por informação
  let sortedProdutos = productosList.filter(produto => {
    // Suportar informacao como array ou string
    const info = produto.informacao;
    const hasTag = (tag) => {
      if (Array.isArray(info)) return info.includes(tag);
      return info === tag;
    };

    if (infoFilter === 'recorrente') return hasTag('recorrente');
    if (infoFilter === 'nao_comprar') return hasTag('nao_comprar');
    return true;
  });

  // Ordenar produtos de acordo com o sortConfig selecionado
  sortedProdutos = sortedProdutos.sort((a, b) => {
    const field = sortConfig.field;
    const direction = sortConfig.direction;

    let valueA, valueB;

    switch (field) {
      case 'estoque':
        valueA = a.estoque || 0;
        valueB = b.estoque || 0;
        break;
      case 'totalVendas':
        valueA = a.totalVendas || 0;
        valueB = b.totalVendas || 0;
        break;
      case 'vendasPeriodo':
        valueA = a.vendasPeriodo || 0;
        valueB = b.vendasPeriodo || 0;
        break;
      case 'lucro':
        valueA = a.lucro || 0;
        valueB = b.lucro || 0;
        break;
      case 'markup':
        valueA = a.markup || 0;
        valueB = b.markup || 0;
        break;
      case 'mediaDiariaAjustada':
        valueA = parseFloat(a.mediaDiariaAjustada) || 0;
        valueB = parseFloat(b.mediaDiariaAjustada) || 0;
        break;
      // Não vendidos: mantém a ordem do backend (capital parado = custo × estoque)
      case 'nao_vendidos':
        valueA = (parseFloat(a.precoCusto) || 0) * Math.max(0, parseInt(a.estoque) || 0);
        valueB = (parseFloat(b.precoCusto) || 0) * Math.max(0, parseInt(b.estoque) || 0);
        break;
      case 'vendasMesAtual':
      default:
        valueA = a.vendasMesAtual || 0;
        valueB = b.vendasMesAtual || 0;
        break;
    }

    if (direction === 'asc') {
      return valueA - valueB;
    } else {
      return valueB - valueA;
    }
  });



  return (
    <Container fluid className="p-4">
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <div>
            <h1 className={styles.pageTitle}>Catálogo E-commerce</h1>
            <p className={styles.pageSubtitle}>Gerencie os produtos da loja online</p>
          </div>
          {/* Faturamento do período + mesmo período do ano passado */}
          <FaturamentoHeader flat />
        </div>

        <div className={styles.pageHeaderActions}>

          {/* ── Botão Lista de Compras ── */}
          <div className="d-flex align-items-center gap-2">
            <button
              onClick={toggleModo}
              title={modoAtivo ? 'Desativar modo seleção' : 'Ativar modo seleção de compras'}
              className={`${styles.pillBtn} ${modoAtivo ? styles.pillBtnGreen : ''}`}
            >
              🛒
              {modoAtivo ? 'Selecionando...' : 'Lista de Compras'}
              {!modoAtivo && totalItens > 0 && (
                <span className={styles.pillBadge}>{totalItens}</span>
              )}
            </button>
            {totalItens > 0 && (
              <button
                onClick={() => setPainelAberto(true)}
                title="Ver lista de compras"
                className={`${styles.pillBtn} ${styles.pillBtnGreen} ${styles.pillBtnSmall}`}
              >
                📋 {totalItens} {totalItens === 1 ? 'item' : 'itens'}
              </button>
            )}
          </div>

          {/* ── Botão Compras Automáticas ── */}
          <button
            onClick={() => setComprasAutoAberto(true)}
            title="Ver, de uma vez, tudo que vendeu mais de 40 no mês e quanto comprar de cada"
            className={`${styles.pillBtn} ${styles.pillBtnPurple}`}
          >
            ⚡ Compras Automáticas
          </button>

          {/* ── Botão Alertas de Vendas ── */}
          <button
            onClick={() => setAlertasAberto(true)}
            title="Itens que quebraram o padrão de venda (queda forte por vários dias) e ainda têm estoque"
            className={`${styles.pillBtn} ${styles.pillBtnRed}`}
          >
            ⚠️ Alertas de Vendas
          </button>

          <ViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />

          {data?.pagination && (
            <div className="d-flex align-items-center gap-3">
              {/* Capital Investido no Estoque */}
              <div className={styles.capitalCard} title="Capital investido no estoque (preço de custo × quantidade)">
                <span className={styles.capitalIcon}>💰</span>
                <div>
                  <div className={styles.capitalLabel}>Capital Investido</div>
                  <div className={styles.capitalValue}>
                    {data.pagination.valorTotalEstoque?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || 'R$ 0,00'}
                  </div>
                </div>
              </div>

              <div className={styles.statBlock}>
                <div className={styles.statBlockValue}>{data.pagination.total?.toLocaleString('pt-BR') || 0}</div>
                <div className={styles.statBlockLabel}>Produtos</div>
              </div>
              <div className={styles.statBlock}>
                <div className={styles.statBlockValue}>{sortedProdutos.length}</div>
                <div className={styles.statBlockLabel}>Exibindo</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Banner de modo ativo */}
      {modoAtivo && (
        <div className={styles.modeBanner}>
          <div className={styles.modeBannerText}>
            <span style={{ fontSize: '1.2rem' }}>🛒</span>
            <span>Modo de seleção ativo — clique em qualquer produto para adicioná-lo à lista</span>
          </div>
          <div className={styles.modeBannerActions}>
            {totalItens > 0 && (
              <button
                onClick={() => { toggleModo(); setPainelAberto(true); }}
                className={styles.modeBannerBtn}
              >📋 Ver lista ({totalItens})</button>
            )}
            <button
              onClick={toggleModo}
              className={styles.modeBannerBtnGhost}
            >✕ Encerrar</button>
          </div>
        </div>
      )}



      <FiltrosEcommerce
        onFilterChange={handleFilterChange}
        onSearch={handleSearch}
        onExportCSV={handleExportCSV}
        initialCategoria={categoria}
        initialInformacao={informacao}
        initialFornecedor={fornecedor}
        initialDataInicio={dataInicio}
        initialDataFim={dataFim}
        initialSearch={search}
        initialMediaDia={mediaDia}
        initialCodigosLote={codigosLote.join('\n')}
        initialOrdenacao={ordenacao}
      />

      {isLoading && <Loading />}

      {!isLoading && isError && (
        <div className="alert alert-danger">
          Erro ao carregar produtos: {error?.message || 'Erro desconhecido'}
        </div>
      )}

      {!isLoading && !isError && sortedProdutos.length === 0 && (
        <EmptyState message="Nenhum produto encontrado" />
      )}

      {!isLoading && !isError && sortedProdutos.length > 0 && (
        <Card className={styles.panel}>
          <Card.Header className={styles.panelHeader}>
            <h6 className={styles.panelHeaderTitle}>Lista de Produtos</h6>
          </Card.Header>
          <Card.Body className={viewMode === 'table' ? styles.panelBodyTable : styles.panelBodyGrid}>
            {viewMode === 'table' ? (
              <div className="table-responsive">
                <ProductTable
                  produtos={sortedProdutos}
                  tipo="ecommerce"
                  onSort={handleSort}
                  sortConfig={sortConfig}
                  onFilterInfo={handleFilterInfo}
                  infoFilter={infoFilter}
                  onOpenTaskModal={handleOpenTaskModal}
                  onOpenInfoModal={handleOpenInfoModal}
                  modoListaCompras={modoAtivo}
                  onClicarProduto={handleClicarProduto}
                  estaNoCarrinho={estaNoCarrinho}
                  getQuantidade={getQuantidade}
                />
              </div>
            ) : (
              <div className={styles.productGrid}>
                {sortedProdutos.map(produto => (
                  <div
                    key={produto.id}
                    onClick={() => handleClicarProduto(produto)}
                    style={modoAtivo ? { cursor: 'pointer' } : {}}
                  >
                    <ProductCard
                      produto={produto}
                      tipo="ecommerce"
                      lojaContexto="ecommerce"
                      onOpenTaskModal={modoAtivo ? undefined : handleOpenTaskModal}
                      onOpenInfoModal={modoAtivo ? undefined : handleOpenInfoModal}
                      hasPendingTasks={pendingTasksMap[produto.codigo]}
                      onMouseEnter={(e) => handleMouseEnter(e, produto)}
                      onMouseLeave={handleMouseLeave}
                      modoListaCompras={modoAtivo}
                      noCarrinho={estaNoCarrinho(produto.codigo || produto.codigoInterno)}
                      qtdNoCarrinho={getQuantidade(produto.codigo || produto.codigoInterno)}
                    />
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
          <Card.Footer className={styles.panelFooter}>
            <Pagination
              currentPage={page}
              totalPages={data.pagination?.totalPages || 1}
              onPageChange={handlePageChange}
            />
          </Card.Footer>
        </Card>
      )}

      {/* Modal de adicionar produto à lista de compras */}
      {produtoSelecionado && (
        <ModalAdicionarItem
          produto={produtoSelecionado}
          onClose={handleFecharModalAdicionar}
        />
      )}

      {/* Painel lateral de lista de compras */}
      {painelAberto && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1040 }}
            onClick={() => setPainelAberto(false)}
          />
          <ListaComprasPanel onClose={() => setPainelAberto(false)} />
        </>
      )}

      {/* Modal Compras Automáticas — top sellers (>40/mês) com quantidade sugerida */}
      <ComprasAutomaticasModal
        isOpen={comprasAutoAberto}
        onClose={() => setComprasAutoAberto(false)}
        onAbrirLista={() => setPainelAberto(true)}
        onAplicarLote={(codigos) => { setCodigosLote(codigos || []); setPage(1); setComprasAutoAberto(false); }}
        adicionarItem={adicionarItem}
        contexto="E-commerce"
      />

      {/* Modal Alertas de Vendas — quebra no padrão de venda (com estoque) */}
      <AlertasVendasModal
        isOpen={alertasAberto}
        onClose={() => setAlertasAberto(false)}
        escopo="ecommerce"
      />

      <TaskModal
        show={taskModalOpen}
        onHide={() => setTaskModalOpen(false)}
        product={taskProduct}
        isEcommerce={true}
      />

      <InfoModal
        show={infoModalOpen}
        onHide={() => setInfoModalOpen(false)}
        produto={infoProduct}
        tipo="ecommerce"
      />

      {/* Unified Tooltip for Grid View */}
      {viewMode === 'grid' && activeProduct && (
        <ProductHoverCard
          produto={activeProduct}
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            maxHeight: tooltipPosition.maxHeight ? `${tooltipPosition.maxHeight}px` : '85vh',
            zIndex: 9999
          }}
        />
      )}
    </Container>
  );
};

export default EcommerceCatalog;
